<template>
  <div class="mdcard-list-wrapper">
    <div class="mdcard-list-grid">
      <div
        v-for="(item, idx) in items"
        :key="item.id || idx"
        class="mdcard-list-item"
      >
        <!-- Logo + Title row -->
        <div class="mdcard-item-head">
          <div class="mdcard-item-logo">
            <img v-if="item.logo && item.logo.startsWith('http')" :src="item.logo" alt="" class="mdcard-logo-img" />
            <i v-else :class="item.logo || getIcon(idx)"></i>
          </div>
          <div class="mdcard-item-head-text">
            <h4 class="mdcard-item-title" :title="item.title">{{ item.title }}</h4>
            <span class="mdcard-item-desc" :title="item.desc">{{ item.desc }}</span>
          </div>
        </div>

        <!-- Content list -->
        <ul class="mdcard-item-content">
          <li v-for="(c, ci) in item.content" :key="ci">
            <span class="mdcard-item-dot"></span>
            <span class="mdcard-item-text" :title="c">{{ c }}</span>
          </li>
        </ul>
        <div v-if="item.footer" class="mdcard-item-footer">
          <span class="mdcard-footer-text">{{ item.footer.text }}</span>
          <span
            class="mdcard-footer-tag"
            :class="'mdcard-footer-tag--' + getTagClass(item.footer.tag.type)"
          >{{ item.footer.tag.text }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'CardList',
  props: {
    data: {
      type: Object,
      required: true,
      default: () => ({ options: [] })
    }
  },
  computed: {
    items() {
      const raw = this.data.options
      if (!raw) return []
      if (Array.isArray(raw)) return raw
      if (typeof raw === 'string') {
        try { return JSON.parse(raw) } catch { return [] }
      }
      return []
    }
  },
  methods: {
    getIcon(idx) {
      const icons = ['el-icon-lightning', 'el-icon-star-on', 'el-icon-refresh', 'el-icon-remove-outline']
      return icons[idx % 4] || 'el-icon-s-flag'
    },
    getTagClass(type) {
      return type || 'info'
    }
  }
}
</script>

<style lang="scss" scoped>
.mdcard-list-wrapper {
  background: #F8FAFC;
  border-radius: 8px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, .06);
  overflow: hidden;
}

.mdcard-list-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
}

.mdcard-list-item {
  padding: 20px 22px;
  border: 1px solid #E4E7ED;
  min-width: 0;
  transition: background .2s;

  &:hover {
    background: #edf0f7;
  }
}

// ---- Card head: logo + title + desc + difficulty ----
.mdcard-item-head {
  display: flex;
  align-items: start;
  gap: 12px;
  margin-bottom: 14px;
}

.mdcard-item-logo {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  background: linear-gradient(135deg, #4a5ff3, #6e7ff9);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 17px;
  color: #fff;
  flex-shrink: 0;
  overflow: hidden;
}

.mdcard-logo-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.mdcard-item-head-text {
  flex: 1;
  min-width: 0;
}

.mdcard-item-title {
  margin: 0 0 4px !important;
  font-size: 14px;
  font-weight: 600;
  color: #303133;
  line-height: 1.4;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.mdcard-item-desc {
  font-size: 12px;
  color: #909399;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  word-break: break-all;
}

// ---- Content list ----
.mdcard-item-content {
  list-style: none;
  margin: 0;
  padding: 0;

  li {
    font-size: 12px;
    color: #606266;
    line-height: 1.9;
    display: flex;
    align-items: flex-start;
    gap: 8px;
    min-width: 0;
  }

  .mdcard-item-text {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    flex: 1;
    min-width: 0;
  }
}

.mdcard-item-dot {
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: #c0c4cc;
  flex-shrink: 0;
  margin-top: 8px;
}

.mdcard-item-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 10px;
  border-top: 1px solid #E4E7ED;
  margin-top: 10px;
}

.mdcard-footer-text {
  font-size: 12px;
  color: #909399;
}

.mdcard-footer-tag {
  font-size: 11px;
  padding: 1px 8px;
  border-radius: 4px;
  border: 1px solid;
  line-height: 1.6;
  white-space: nowrap;

  &--success {
    color: #67C23A;
    background: #f0f9eb;
    border-color: #e1f3d8;
  }
  &--primary {
    color: #409EFF;
    background: #ecf5ff;
    border-color: #d9ecff;
  }
  &--warning {
    color: #E6A23C;
    background: #fdf6ec;
    border-color: #faecd8;
  }
  &--danger {
    color: #F56C6C;
    background: #fef0f0;
    border-color: #fde2e2;
  }
  &--info {
    color: #909399;
    background: #f4f4f5;
    border-color: #e9e9eb;
  }
}
</style>
